import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import axiosInstance from '../../config/axiosApi/axiosApi';
import APIs from '../../Constants/apis';
import { login } from '../../app/auth/authSlice';
import showErrorToast, { showSuccessToast } from '../../components/CustomComponents/toast/toast';
import userDataService from '../../services/userDataService';
import './AutoLogin.css';

const AutoLogin = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const redirectTimerRef = useRef(null);

  const clearRedirectTimer = () => {
    if (redirectTimerRef.current) {
      clearTimeout(redirectTimerRef.current);
      redirectTimerRef.current = null;
    }
  };

  const goToLoginNow = () => {
    clearRedirectTimer();
    navigate('/user/login', { replace: true });
  };

  useEffect(() => {
    const processAutoLogin = async () => {
      try {
        const token = searchParams.get('token');
        const compId = searchParams.get('compId');

        if (!token) {
          setErrorMessage('Invalid or missing auto-login token');
          setLoading(false);
          redirectTimerRef.current = setTimeout(() => {
            navigate('/user/login', { replace: true });
          }, 3000);
          return;
        }

        const response = await axiosInstance.post(APIs.AUTO_LOGIN, {
          Token: token,
          CompId: compId ? parseInt(compId) : null
        });

        if (response.data.status && response.data.data) {
          const UserData = response.data.data.UserData || response.data.data.userData;
          const CompanyData = response.data.data.CompanyData || response.data.data.companyData;
          const IsAutoLogin = response.data.data.IsAutoLogin || response.data.data.isAutoLogin || true;

          if (!UserData) {
            setErrorMessage('User data missing from server response');
            showErrorToast('Auto-login failed: Invalid user data');
            setLoading(false);
            redirectTimerRef.current = setTimeout(() => navigate('/user/login', { replace: true }), 3000);
            return;
          }

          const initialUserData = {
            userData: UserData,
            keepMeLogin: false,
            isAutoLogin: IsAutoLogin || false
          };
          dispatch(login(initialUserData));
          localStorage.setItem("keepMeLogin", JSON.stringify(false));

          try {
            const userDataResponse = await userDataService.fetchUserData({
              allowPartial: true,
              silent: true,
              skipUnauthorizedRedirect: true
            });
            const completeUserData = {
              userData: UserData,
              keepMeLogin: false,
              isAutoLogin: IsAutoLogin || false,
              profileData: userDataResponse.profileData,
              settingsData: userDataResponse.settingsData,
              lastDataRefresh: userDataResponse.lastDataRefresh
            };
            dispatch(login(completeUserData));
          } catch (error) {
            const fallbackUserData = {
              userData: UserData,
              keepMeLogin: false,
              isAutoLogin: IsAutoLogin || false,
              profileData: null,
              settingsData: null,
              lastDataRefresh: new Date().toISOString()
            };
            dispatch(login(fallbackUserData));
          }

          showSuccessToast(`Welcome to ${CompanyData?.compName || 'BackOffice'}! (Auto-Login)`);

          redirectTimerRef.current = setTimeout(() => {
            navigate('/dashboard', { replace: true });
          }, 1500);
        } else {
          setErrorMessage(response.data.message || 'Auto-login failed');
          showErrorToast(response.data.message || 'Auto-login failed');
          setLoading(false);
          redirectTimerRef.current = setTimeout(() => {
            navigate('/user/login', { replace: true });
          }, 3000);
        }
      } catch (error) {
        const errorMsg = error.response?.data?.message || 'Auto-login failed. Please try again.';
        setErrorMessage(errorMsg);
        showErrorToast(errorMsg);
        setLoading(false);

        redirectTimerRef.current = setTimeout(() => {
          navigate('/user/login', { replace: true });
        }, 3000);
      }
    };

    processAutoLogin();

    return () => clearRedirectTimer();
  }, [searchParams, navigate, dispatch]);

  return (
    <div className="auto-login-page">
      {loading ? (
        <div className="auto-login-card" role="status" aria-live="polite">
          <div className="auto-login-icon auto-login-icon--pulse" aria-hidden="true">
            <i className="fa fa-shield-alt" />
          </div>
          <div className="auto-login-brand">ServeIQ BackOffice</div>
          <h1 className="auto-login-title">Signing you in</h1>
          <p className="auto-login-subtitle">
            Please wait while we securely open this company session.
          </p>
          <div className="auto-login-progress" aria-hidden="true">
            <span />
            <span />
            <span />
          </div>
          <p className="auto-login-footnote">
            Secure auto-login from Super Admin
          </p>
        </div>
      ) : (
        <div className="auto-login-card auto-login-card--error" role="alert">
          <div className="auto-login-icon auto-login-icon--error" aria-hidden="true">
            <i className="fa fa-exclamation-triangle" />
          </div>
          <div className="auto-login-brand">ServeIQ BackOffice</div>
          <h1 className="auto-login-title">Auto-login failed</h1>
          <p className="auto-login-subtitle">
            We could not complete the secure sign-in for this company.
          </p>
          {errorMessage ? (
            <p className="auto-login-error-msg">{errorMessage}</p>
          ) : null}
          <p className="auto-login-footnote mb-3">
            Redirecting to the login page…
          </p>
          <div className="auto-login-actions">
            <button
              type="button"
              className="auto-login-btn auto-login-btn--primary"
              onClick={goToLoginNow}
            >
              <i className="fa fa-sign-in-alt" aria-hidden="true" />
              Go to login now
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AutoLogin;
